import Grid from '@mui/material/Grid';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { Action } from 'redux';
import type { AppState } from '../../state/AppState';
import {
  reportAccountingInformationFilterEndBusinessDateSelector,
  reportAccountingInformationFilterStartBusinessDateSelector,
  changeAccountingInformationFilterEndBusinessDate,
  changeAccountingInformationFilterStartBusinessDate,
} from '../../state';
import {
  CountryFilter,
  ReportActionsGridItem,
  ReportFilterActions,
  ReportFilters,
  StoreFilter,
} from '../ReportFilters';
import { AppPermissions } from '../../types';
import { MonthOnlyDateFilter } from '../CollectionsFilters';
import { PermissionsProvider } from '../Permissions';
import { FilterPanel, StatusChip } from '../Visual';

import type { Stores } from '../../types';
import { useOpStructureValidation } from '../../hooks';

type Props = {
  applyAction: (storesForCompany: Stores[]) => Action;
  onedriveAction: (storesForCompany: Stores[]) => Action;
  loadingSelector: (state: AppState) => boolean;
  resourcePermissions: AppPermissions[];
  subtitleTranslationKey: string;
};

const AccountingInformationReportBaseSearch = ({
  applyAction,
  onedriveAction,
  loadingSelector,
  resourcePermissions,
  subtitleTranslationKey,
}: Props) => {
  const { t } = useTranslation();
  const startDate = useSelector(reportAccountingInformationFilterStartBusinessDateSelector);
  const endDate = useSelector(reportAccountingInformationFilterEndBusinessDateSelector);
  const hasCountry = Boolean(useSelector((state: AppState) => state.report?.filters?.countryCode));
  const applyDisabled = !(startDate?.accept && endDate?.accept && hasCountry);
  const countryRequiredIncomplete = applyDisabled && !hasCountry;
  const monthRequiredIncomplete = applyDisabled && !(startDate?.accept && endDate?.accept);
  const onedriveDisabled = applyDisabled;

  const validateAndExecuteAction = useOpStructureValidation();

  const validatedApplyAction = useCallback(
    () => validateAndExecuteAction(applyAction),
    [validateAndExecuteAction, applyAction]
  );

  const validatedOnedriveAction = useCallback(
    () => validateAndExecuteAction(onedriveAction),
    [validateAndExecuteAction, onedriveAction]
  );

  return (
    <PermissionsProvider resourcePermissions={resourcePermissions}>
      <ReportFilters
        loadingSelector={loadingSelector}
        applyAction={validatedApplyAction}
        onedriveAction={validatedOnedriveAction}
        showOpStructureFilters
      >
        <FilterPanel
          subtitleTranslationKey={subtitleTranslationKey}
          actions={
            <StatusChip
              status={applyDisabled ? 'warning' : 'success'}
              label={
                applyDisabled ? t('filters.status.missingRequired') : t('filters.status.ready')
              }
            />
          }
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3} lg={2} xl={2}>
              <CountryFilter fullWidth requiredIncomplete={countryRequiredIncomplete} />
            </Grid>
            <Grid item xs={12} md={3} lg={3} xl={2}>
              <StoreFilter />
            </Grid>
            <Grid item xs={12} md={3} lg={2} xl={2}>
              <MonthOnlyDateFilter
                requiredIncomplete={monthRequiredIncomplete}
                startDateSelector={reportAccountingInformationFilterStartBusinessDateSelector}
                endDateSelector={reportAccountingInformationFilterEndBusinessDateSelector}
                changeStartDateAction={changeAccountingInformationFilterStartBusinessDate}
                changeEndDateAction={changeAccountingInformationFilterEndBusinessDate}
              />
            </Grid>
            <ReportActionsGridItem>
              <ReportFilterActions
                applyDisabled={applyDisabled}
                onedriveDisabled={onedriveDisabled}
              />
            </ReportActionsGridItem>
          </Grid>
        </FilterPanel>
      </ReportFilters>
    </PermissionsProvider>
  );
};

export default AccountingInformationReportBaseSearch;
